import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { observable, runInAction } from 'mobx';
import { barTitleOf } from '@featit/shared/bar-title';
import { englishT } from '@/i18n/LocaleProvider';
import { pageT } from '@/i18n/pageTranslator';
import type { IRouteParent } from './routeParents';
import './shellWords';

/**
 * The title of the record a screen shows, as the screen announces it (I-1252).
 *
 * The phone's back row is shell chrome, mounted once in `Layout.tsx` and read off the address
 * (`routeParents.ts`), so it knows a pushed screen's kind and key but not its record's name — a
 * document's back row says only "Document". Its ⋯ shares the record, and a link shared under
 * "Document" is no help to whoever receives it. A screen that has its record's name announces it
 * here for its own address; the back row reads it for the screen it is drawn over.
 *
 * A screen whose read settled without a name says how it settled instead, so the back row's Share
 * never waits for a name that will not come (I-1252, the captain's ruling: a pending control that
 * never resolves is a frozen UI).
 */
/**
 * How a screen's read of its record settled without a name: `failed` (the read failed — the server,
 * the network, or a read refused) or `missing` (the address names no record).
 */
export type UnnamedRecordType = 'failed' | 'missing';

interface IAnnouncement {
  path: string;
  title: string | null;
  unnamed: UnnamedRecordType | null;
}

const announced = observable.box<IAnnouncement | null>(null, { deep: false });

/**
 * Announces `title` as the record of the screen at the current address, while it is mounted — or,
 * with no title, that its read settled without one (`unnamed`).
 */
export function useAnnounceRecordTitle(
  title: string | null | undefined,
  unnamed: UnnamedRecordType | null = null,
): void {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (!title && !unnamed) return;
    const entry: IAnnouncement = {
      path: pathname,
      title: title || null,
      unnamed: title ? null : unnamed,
    };
    runInAction(() => announced.set(entry));
    return () => {
      runInAction(() => {
        if (announced.get() === entry) announced.set(null);
      });
    };
  }, [pathname, title, unnamed]);
}

function announcementFor(pathname: string): IAnnouncement | null {
  const entry = announced.get();
  return entry && entry.path === pathname ? entry : null;
}

/** The title announced for the screen at `pathname`, or null. Observable (MobX). */
export function announcedRecordTitle(pathname: string): string | null {
  return announcementFor(pathname)?.title ?? null;
}

/** The record kinds whose name is an item's: its key and title, once the item store holds it. */
const ITEM_RECORDS = new Set<IRouteParent['recordType']>(['item', 'decision', 'task']);
/** The pages whose bar names only their kind until the screen announces the record's name. */
const ANNOUNCED_VIEWS = new Set(['document', 'page']);

/** What the back row's Share can do for the screen it is drawn over. */
export type BackRowShareType =
  | { state: 'ready'; title: string }
  | { state: 'pending' }
  | { state: 'refused'; reason: string };

/**
 * What the back row's Share posts for the screen at `pathname` (I-1252, reopened: pressed the moment
 * ⋯ appeared, Share posted "T-0003" and "Document").
 *
 * - An item, a decision, a task: its key and title — from `item` (the item store) or its screen's
 *   announcement. A document or a custom page: the name its screen announces. Until then `pending`.
 * - A read that **failed** settles on the best title known, the back row's key: the address is still
 *   the record's, the failure is this reader's (a server or network fault, or a read refused to
 *   them), and whoever receives the link opens it with their own access.
 * - An address that names **no record** is `refused`: there is nothing to send, and a link to it
 *   would only lead the recipient to the same not-found page.
 * - Anything else (a part, Usage, a settings page, Revise): the back row's key is its name already.
 */
export function backRowShare(
  parent: Pick<IRouteParent, 'recordKey' | 'recordType'>,
  pathname: string,
  item: { key: string; title: string } | undefined,
): BackRowShareType {
  if (item) return { state: 'ready', title: `${item.key} ${item.title}` };
  // Revise, pushed over a decision, is named for itself, not by the address's key.
  const lastSegment = decodeURIComponent(pathname.split('/').filter(Boolean).pop() ?? '');
  const namedLater = ITEM_RECORDS.has(parent.recordType)
    ? lastSegment === parent.recordKey
    : parent.recordType === 'page' && ANNOUNCED_VIEWS.has(barTitleOf(pathname).view ?? '');
  if (!namedLater) return { state: 'ready', title: parent.recordKey };
  const announcement = announcementFor(pathname);
  if (announcement?.title) return { state: 'ready', title: announcement.title };
  if (announcement?.unnamed === 'failed') return { state: 'ready', title: parent.recordKey };
  if (announcement?.unnamed === 'missing') {
    // I-1925: in the page's language, its English word for word.
    return { state: 'refused', reason: pageT(englishT)('shell.recordBar.nothingToShare') };
  }
  return { state: 'pending' };
}
